"use client";

import { useToast } from "@/components/ui/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import { useEffect, useState } from "react";

const toastIcons = {
  information: <Info className="h-6 w-6 text-blue-500" />,
  success: <CheckCircle className="h-6 w-6 text-green-500" />,
  default: <CheckCircle className="h-6 w-6 text-green-500" />,
  error: <AlertCircle className="h-6 w-6 text-red-500" />,
  destructive: <AlertCircle className="h-6 w-6 text-red-500" />,
  alert: <AlertCircle className="h-6 w-6 text-yellow-500" />,
};

const toastColors = {
  information: "bg-blue-500",
  success: "bg-green-500",
  default: "bg-green-500",
  error: "bg-red-500",
  destructive: "bg-red-500",
  alert: "bg-yellow-500",
};

export function BeautifulToaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, ...props }) => {
        const Icon =
          toastIcons[props.variant as keyof typeof toastIcons] ||
          toastIcons.information;
        const progressColor =
          toastColors[props.variant as keyof typeof toastColors] ||
          toastColors.information;

        return (
          <Toast
            key={id}
            {...props}
            className="flex flex-col gap-1 overflow-hidden bg-white jutify-start items-start"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">{Icon}</div>
              <div className="flex-grow">
                <ToastTitle className="text-base font-bold mb-1">
                  {props.variant === "success" && "Sucesso!"}
                  {props.variant === "error" && "Erro!"}
                  {props.variant === "alert" && "Alerta!"}
                  {props.variant === "information" && "Informação!"}
                  {props.variant === "destructive" && "Erro!"}
                  {props.variant === "default" && "Sucesso!"}
                </ToastTitle>
                <ToastDescription className="text-sm text-left break-words ">
                  {description}
                </ToastDescription>
              </div>
            </div>
            {action && <div className="mt-2">{action}</div>}
            <ToastClose />
            <div className="w-full mt-2">
              <ProgressBar color={progressColor} />
            </div>
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

function ProgressBar({ color }: { color: string }) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prevProgress - 1;
      });
    }, 50);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
      <div
        className={`h-full ${color} transition-all duration-100 ease-linear`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
